import React, { useState, useEffect, useRef } from 'react';
import { ChatService } from '../services/chat';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const ChatWindow = ({ roomId, userId, username, onClose }) => {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [chatService] = useState(() => new ChatService());
  const [isConnected, setIsConnected] = useState(false);
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [emotes, setEmotes] = useState([]);
  const [showEmotePicker, setShowEmotePicker] = useState(false);
  const [replyingTo, setReplyingTo] = useState(null);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    connectToChat();
    loadEmotes();
    loadChatHistory();
    loadOnlineUsers();

    return () => {
      chatService.disconnect();
    };
  }, [roomId]);

  const connectToChat = async () => {
    try {
      await chatService.connect(roomId, userId, username);
      setIsConnected(true);

      chatService.onMessage((msg) => {
        if (msg.type === 'error') {
          alert(msg.message);
        } else {
          setMessages(prev => [...prev, msg]);
          scrollToBottom();
        }
      });
    } catch (error) {
      console.error('Failed to connect to chat:', error);
      alert('Failed to connect to chat');
    }
  };

  const loadEmotes = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/chat/emotes`);
      setEmotes(data);
    } catch (error) {
      console.error('Error loading emotes:', error);
    }
  };

  const loadChatHistory = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/chat/history/${roomId}?limit=50`);
      setMessages(data);
      scrollToBottom();
    } catch (error) {
      console.error('Error loading chat history:', error);
    }
  };

  const loadOnlineUsers = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/chat/online?room_id=${roomId}`);
      setOnlineUsers(data);
    } catch (error) {
      console.error('Error loading online users:', error);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const sendMessage = (e) => {
    e.preventDefault();
    if (!inputMessage.trim() || !isConnected) return;

    chatService.sendMessage(inputMessage, replyingTo?.message_id);
    setInputMessage('');
    setReplyingTo(null);
  };

  const sendEmote = (emoteId) => {
    chatService.sendEmote(emoteId);
    setShowEmotePicker(false);
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const renderMessage = (msg) => {
    const isOwn = msg.user_id === userId;
    const isSystem = msg.message_type === 'system';

    if (isSystem) {
      return (
        <div className="flex justify-center my-2">
          <div className="px-3 py-1 bg-gray-800/50 rounded-full text-xs text-gray-400">
            {msg.message}
          </div>
        </div>
      );
    }

    return (
      <div className={`flex ${isOwn ? 'justify-end' : 'justify-start'} mb-3`}>
        <div className={`max-w-[70%] ${
          isOwn ? 'bg-gradient-to-r from-purple-600 to-pink-600' : 'bg-gray-800'
        } rounded-lg p-3`}>
          {!isOwn && (
            <div className="text-xs text-purple-400 font-bold mb-1">
              {msg.username}
            </div>
          )}
          {msg.replied_to && (
            <div className="mb-2 p-2 bg-black/30 rounded text-xs text-gray-400 border-l-2 border-gray-600">
              Replying to previous message
            </div>
          )}
          {msg.message_type === 'emote' ? (
            <div className="text-4xl">{msg.message.replace('[emote:', '').replace(']', '')}</div>
          ) : (
            <div className="text-white break-words">{msg.message}</div>
          )}
          <div className="text-xs text-gray-400 mt-1">
            {formatTime(msg.timestamp)}
          </div>
          {!isOwn && (
            <button
              onClick={() => setReplyingTo(msg)}
              className="text-xs text-purple-400 hover:text-purple-300 mt-1"
            >
              Reply
            </button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="fixed bottom-4 right-4 w-96 h-[600px] bg-gray-900 rounded-lg shadow-2xl border-2 border-purple-500 flex flex-col z-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-pink-600 p-4 rounded-t-lg flex items-center justify-between">
        <div>
          <div className="text-white font-bold text-lg">Chat Room</div>
          <div className="text-xs text-purple-200">
            {isConnected ? `🟢 ${onlineUsers.length} online` : '🔴 Connecting...'}
          </div>
        </div>
        <button
          onClick={onClose}
          className="text-white hover:text-gray-300 text-2xl transition-colors"
        >
          ×
        </button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-2 bg-gray-950">
        {messages.map((msg, index) => (
          <div key={msg.message_id || index}>
            {renderMessage(msg)}
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      {/* Reply Preview */}
      {replyingTo && (
        <div className="bg-gray-800 px-4 py-2 border-t border-gray-700 flex items-center justify-between">
          <div className="text-sm text-gray-400">
            Replying to <span className="text-purple-400">{replyingTo.username}</span>
          </div>
          <button
            onClick={() => setReplyingTo(null)}
            className="text-gray-400 hover:text-white"
          >
            ×
          </button>
        </div>
      )}

      {/* Emote Picker */}
      {showEmotePicker && (
        <div className="bg-gray-800 p-4 border-t border-gray-700">
          <div className="grid grid-cols-6 gap-2">
            {emotes.map((emote) => (
              <button
                key={emote.id}
                onClick={() => sendEmote(emote.id)}
                className="text-3xl hover:scale-125 transition-transform"
                title={emote.name}
              >
                {emote.icon}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Input */}
      <form onSubmit={sendMessage} className="p-4 bg-gray-900 border-t border-gray-700 rounded-b-lg">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setShowEmotePicker(!showEmotePicker)}
            className="px-3 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg text-2xl transition-colors"
          >
            😃
          </button>
          <input
            type="text"
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            placeholder="Type a message..."
            className="flex-1 px-4 py-2 bg-gray-800 text-white rounded-lg border border-gray-700 focus:border-purple-500 focus:outline-none"
            disabled={!isConnected}
          />
          <button
            type="submit"
            disabled={!inputMessage.trim() || !isConnected}
            className="px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 rounded-lg font-bold transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Send
          </button>
        </div>
      </form>
    </div>
  );
};

export default ChatWindow;